import { useCertificationStats } from "@/queries";
import { createPortal } from "react-dom";
import { LuHistory, LuLock, LuLockOpen } from "react-icons/lu";
import LockSelection from "../Revisions/LockSelection";
import RevisionHistoryTable from "../Revisions/RevisionTableHistory";
import useRevisions from "../Revisions/hooks/useRevisions";
import { AnomalyRun } from "@/models/file-submission";

import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";

export type Revision = {
  id: string;
  revisionCreateDate: string;
  activityDate: string;
  revisionStatus: "Locked" | "Uncertified" | "Certified" | "Decertified" | "";
  totalRecords: number;
  totalErrors: number;
  totalWarnings: number;
  ssidErrorCount: number;
  midErrorCount: number;
  uniqueCSPPIDCount: number;
};

type RevisionsTabProps = {
  esiid?: string;
  esiidPath: string;
  certificationId?: string;
  lockedAnomalyRunId: string | null;
  certificationAnomalyRun: AnomalyRun | null;
  certificationStatus: "Uncertified" | "Certified" | "Decertified";
  isCycleEnded: boolean;
  canCertify: boolean;
};

function mapStatsToRevision(
  stat: any,
  lockedAnomalyRunId: string | null
): Revision {
  const totalRecords =
    (stat.TotalCHLDRecords ?? 0) +
    (stat.TotalSTFFRecords ?? 0) +
    (stat.TotalCLRMRecords ?? 0);

  const isLocked =
    lockedAnomalyRunId !== null &&
    String(stat.AnomalyRunId) === lockedAnomalyRunId;

  return {
    id: String(stat.AnomalyRunId),
    revisionCreateDate: stat.AnomalyRun?.StartTime ?? "",
    activityDate: stat.AnomalyRun?.EndTime ?? "",
    revisionStatus: isLocked ? "Locked" : "Uncertified",
    totalRecords,
    totalErrors: stat.Errors ?? 0,
    totalWarnings: stat.Warnings ?? 0,
    ssidErrorCount: 0,
    midErrorCount: 0,
    uniqueCSPPIDCount: 0,
  };
}

function buildRevisionFromAnomalyRun(run: AnomalyRun): Revision {
  return {
    id: String(run.Id),
    revisionCreateDate: run.StartTime ?? "",
    activityDate: run.EndTime ?? "",
    revisionStatus: "Locked",
    totalRecords: 0,
    totalErrors: run.Detected ?? 0,
    totalWarnings: 0,
    ssidErrorCount: 0,
    midErrorCount: 0,
    uniqueCSPPIDCount: 0,
  };
}

export default function RevisionsTab({
  esiid,
  esiidPath,
  certificationId,
  lockedAnomalyRunId,
  certificationAnomalyRun,
  certificationStatus,
  isCycleEnded,
  canCertify,
}: RevisionsTabProps) {
  const certStats = useCertificationStats(esiidPath, certificationId ?? "");

  let revisionHistory: Revision[] = [];
  let mostRecentRevision: Revision | null = null;
  let lockedRevision: Revision | null = null;

  if (
    certStats.data &&
    Array.isArray(certStats.data) &&
    certStats.data.length > 0
  ) {
    const sorted = [...certStats.data].sort(
      (a: any, b: any) => b.AnomalyRunId - a.AnomalyRunId
    );

    revisionHistory = sorted.map((stat) =>
      mapStatsToRevision(stat, lockedAnomalyRunId)
    );

    mostRecentRevision = revisionHistory[0] ?? null;
    lockedRevision =
      revisionHistory.find((r) => r.revisionStatus === "Locked") ?? null;

    if (
      lockedRevision &&
      mostRecentRevision &&
      lockedRevision.id === mostRecentRevision.id
    ) {
      mostRecentRevision = null;
    }
  }

  if (!lockedRevision && lockedAnomalyRunId && certificationAnomalyRun) {
    lockedRevision = buildRevisionFromAnomalyRun(certificationAnomalyRun);
  }

  const {
    lockedRevision: resolvedLockedRevision,
    lockedRevisionId,
    isCertified,
    mostRecentHasErrors,
    lockDisabled,
    lockDisabledReason,
    unlockDisabled,
    unlockDisabledReason,
    toast,
    clearToast,
    handleLock,
    handleUnlock,
    handleReplaceLock,
    isLocking,
    isUnlocking,
    lockError,
    unlockError,
  } = useRevisions({
    esiid: esiid ?? "",
    esiidPath,
    certificationId: certificationId ?? "",
    mostRecentRevision,
    lockedRevision,
    revisionHistory,
    certificationStatus,
    isCycleEnded,
  });

  if (certStats.error) {
    return (
      <p className="text-danger">
        Error loading stats: {certStats.error.message}
      </p>
    );
  }

  return (
    <div>
      {!isCertified && (
        <LockSelection
          mostRecentRevision={mostRecentRevision}
          lockedRevision={resolvedLockedRevision}
          lockedRevisionId={lockedRevisionId}
          lockDisabled={lockDisabled}
          lockDisabledReason={lockDisabledReason}
          unlockDisabled={unlockDisabled}
          unlockDisabledReason={unlockDisabledReason}
          hasErrors={mostRecentHasErrors}
          canCertify={canCertify}
          isLocking={isLocking}
          isUnlocking={isUnlocking}
          lockError={lockError}
          unlockError={unlockError}
          onLock={handleLock}
          onUnlock={handleUnlock}
          onReplaceLock={handleReplaceLock}
        />
      )}

      <div className="mt-5 pt-2">
        <div className="d-flex align-items-center gap-2 mb-3">
          <LuHistory size={16} className="text-muted" />
          <span
            className="fw-bold text-uppercase text-muted"
            style={{ fontSize: "0.85rem", letterSpacing: "0.5px" }}
          >
            Revision History
          </span>
        </div>
        <RevisionHistoryTable revisions={revisionHistory} />
      </div>

      {toast &&
        createPortal(
          <ToastContainer
            position="top-end"
            className="p-3"
            style={{ zIndex: 2000 }}
          >
            <Toast
              show
              onClose={clearToast}
              delay={3000}
              autohide
              className="toastSuccessCard"
            >
              <div className="toastSuccessInner">
                <div className="toastSuccessStrip" />

                <div className="toastSuccessMain">
                  <div className="toastSuccessIcon">
                    {toast.variant === "lock" ? (
                      <LuLock size={20} />
                    ) : (
                      <LuLockOpen size={20} />
                    )}
                  </div>

                  <div className="toastSuccessText">
                    <div className="toastSuccessTitle">{toast.title}</div>
                    <div className="toastSuccessMessage">{toast.message}</div>
                  </div>

                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={clearToast}
                  />
                </div>
              </div>
            </Toast>
          </ToastContainer>,
          document.body
        )}
    </div>
  );
}
